import PracticeProgramCard from '@/app/recommend-class/components/practice-program-card';
import ImageOptimizer from '@/components/image-optimizer';
import { withWeek2UnlimitedPrices } from '@/app/recommend-class/hooks/use-week2-unlimited-card';
import { monthUnlimitedData } from '@/data/recommend-class/unlimited.data';
import {
  MONTH_UNLIMITED_DISCOUNT_DATE_START,
  MONTH_UNLIMITED_DISCOUNT_DATE_END,
  isDateBetween,
} from '@/lib/date';
import { formatKoreanCurrency } from '@/lib/utils';
import { getServerTime } from '@/services/base-api';
import { usePricingList } from '@/services/query/pricing.query';
import { PricingCategoryCode } from '@/services/types/pricing.type';
import Link from 'next/link';

interface UnlimitedPricingProps {
  id: string;
  recommendPrice: string;
}

export default function UnlimitedPricing({
  id,
  recommendPrice,
}: UnlimitedPricingProps) {
  const { data: pricingList } = usePricingList();
  const unlimitedPricing = pricingList?.data.find(
    (pricing) => pricing.code === PricingCategoryCode.UNLIMITED,
  );

  // 공용 데이터(monthUnlimitedData)를 직접 고치지 않고 사본에 서버 요금을 싣는다.
  // 직접 고치면 서버 프로세스에 남아 다음 요청의 다른 화면이 서버에선 바뀐 값, 브라우저에선 원래 값으로
  // 그려져 hydration 오류(React 418)가 났다(2026-10-19). 2주완성 카드는 다른 추천 클래스와 같은 훅을 쓴다.
  const week2CardData = withWeek2UnlimitedPrices(pricingList?.data);
  const monthCardData = {
    ...monthUnlimitedData,
    prices: monthUnlimitedData.prices.map((price) => ({ ...price })),
  };

  // code가 unlimited이고, plans 중 prices는 label이 '1개월 무제한'인 것만 포함
  const monthUnlimitedPlans =
    unlimitedPricing?.plans
      ?.filter((plan) =>
        plan.prices?.some((price) => price.label === '1개월 무제한'),
      )
      ?.map((plan) => ({
        ...plan,
        prices:
          plan.prices?.filter((price) => price.label === '1개월 무제한') ?? [],
      })) ?? [];
  //console.log(`monthUnlimitedPlans : ${JSON.stringify(monthUnlimitedPlans)}`);

  // 할인 기간 여부 (특정 기간에만 discountAmount, discountBanner 적용)
  if (
    isDateBetween(
      getServerTime(),
      MONTH_UNLIMITED_DISCOUNT_DATE_START,
      MONTH_UNLIMITED_DISCOUNT_DATE_END,
    )
  ) {
    monthCardData.discountBanner = '수험생 인증 시 3만원할인!';
    monthCardData.discountAmount = 30000;
  } else {
    monthCardData.discountBanner = '';
    monthCardData.discountAmount = 0;
  }

  // 1개월 무제한 요금 서버 데이터와 매칭
  for (const price of monthCardData.prices) {
    for (const plan of monthUnlimitedPlans) {
      if (!plan.prices?.length) continue;
      const planLabel = plan.label.replace(' ', '');
      let planSubLabel = plan.subLabel.replace('(', '');
      planSubLabel = planSubLabel.replace(')', '');
      const planType = planLabel + ' ' + planSubLabel;
      if (price.type === planType) {
        if (monthCardData.discountAmount > 0) {
          price.originalPrice = formatKoreanCurrency(plan.prices[0].price);
          price.price = formatKoreanCurrency(
            plan.prices[0].price - (monthCardData.discountAmount ?? 0),
          );
        } else {
          price.originalPrice = formatKoreanCurrency(
            plan.prices[0].originPrice,
          );
          price.price = formatKoreanCurrency(plan.prices[0].price);
        }
        break;
      }
    }
  }

  return (
    <div id={id}>
      <div className="bg-main-orange typo-14 desktop:typo-22 flex h-[60px] items-center justify-center font-bold text-white">
        수강생 84%가 선택한 베스트셀러! 면허 취득까지 무제한 연습!
      </div>
      <div className="desktop:mt-20 desktop:mb-10 mt-25 mb-6 flex justify-center">
        <span className="bg-main-yellow typo-20 desktop:typo-40 desktop:py-5 desktop:w-[650px] w-[327px] rounded-full py-3 text-center font-semibold">
          자동차운전전문학원의{' '}
          <span className="font-extrabold">약 1/4 가격!!</span>
        </span>
      </div>

      {/** 웹 */}
      <div className="text-level-3 typo-24 desktop:flex hidden flex-col px-4 text-center leading-normal font-semibold">
        <span>
          2주간 드라이빙존을{' '}
          <span className="font-extrabold">
            무제한 이용할 수 있는 이용권을 {recommendPrice}에 이용
          </span>
          할 수 있어요!
        </span>
        <span>
          일반운전면허학원 대비{' '}
          <span className="font-extrabold">
            1/4 가격이지만 알찬 커리큘럼으로 진행할 수 있어요.
          </span>
        </span>
      </div>
      {/** 모바일 */}
      <div className="text-level-2 typo-18 desktop:hidden flex flex-col px-4 text-center leading-normal font-semibold">
        <span>
          2주간 드라이빙존을{' '}
          <span className="font-bold">무제한 이용할 수 있는</span>
        </span>
        <span>
          <span className="font-bold">이용권을 {recommendPrice}에 이용</span>할
          수 있어요!
        </span>
        <span>
          일반운전면허학원 대비{' '}
          <span className="font-bold">1/4 가격이지만</span>{' '}
        </span>
        <span className="font-bold">알찬 커리큘럼으로 진행할 수 있어요.</span>
      </div>

      {/** 웹 */}
      <div className="desktop:flex mt-20 hidden items-center justify-center">
        <div className="relative flex h-[330px] w-[528px] flex-col items-center justify-center gap-5 bg-[url('/images/main/web_why_price_bg2.webp')] bg-contain bg-center bg-no-repeat">
          <div className="typo-40 pt-5 font-extrabold text-white">
            드라이빙존 운전면허
          </div>
          <div className="text-main-yellow text-[60px] leading-none font-black">
            {recommendPrice}
          </div>
        </div>
        <div className="-ml-8 flex h-[270px] w-[438px] flex-col items-center justify-center gap-5 bg-[url('/images/main/web_why_price_bg1.webp')] bg-contain bg-center bg-no-repeat">
          <div className="text-font-grayblue typo-28 font-semibold">
            운전면허학원 평균수강료
          </div>
          <div className="text-font-grayblue typo-40 font-extrabold">
            800,000원
          </div>
        </div>
      </div>
      {/** 모바일 */}
      <div className="desktop:hidden mt-10">
        <div className="flex h-[144px] flex-col items-center justify-center gap-3 bg-[url('/images/main/why_price_bg1.png')] bg-contain bg-center bg-no-repeat">
          <div className="text-font-grayblue typo-20 font-semibold">
            운전면허학원 평균수강료
          </div>
          <div className="text-font-grayblue typo-32 font-extrabold">
            800,000원
          </div>
        </div>
        <div className="relative flex h-[212px] flex-col items-center justify-center gap-3 bg-[url('/images/main/why_price_bg2.png')] bg-contain bg-center bg-no-repeat">
          <div className="typo-32 pt-4 font-extrabold text-white">
            드라이빙존 운전면허
          </div>
          <div className="typo-48 text-main-yellow font-black">
            {recommendPrice}
          </div>
        </div>
      </div>
      <div>
        <div
          className="desktop:h-[208px] h-[152px]"
          style={{
            backgroundImage: `url('/images/recommend-class/common/price_bg_pattern.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'bottom',
            backgroundRepeat: 'repeat-x',
          }}
        ></div>
      </div>

      <div className="relative bg-[#F8F8F8]">
        <div
          className="absolute right-0 left-0 h-[168px]"
          style={{
            top: 'calc(50% - 218px)',
            backgroundImage: `url('/images/recommend-class/common/book_bg.svg')`,
            backgroundSize: '783px 168px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat-x',
          }}
        ></div>
        <div
          className="absolute right-0 left-0 h-[168px]"
          style={{
            top: 'calc(50% + 50px)',
            backgroundImage: `url('/images/recommend-class/common/book_bg.svg')`,
            backgroundSize: '783px 168px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat-x',
          }}
        ></div>
        <div className="desktop:py-[140px] relative z-10 flex flex-col items-center justify-center py-[100px]">
          <div className="desktop:flex-row desktop:gap-3 typo-28 desktop:typo-40 text-level-1 flex flex-col items-center justify-center gap-1.5 text-center font-semibold">
            <span>내 상황에 맞게 선택하는</span>
            <span className="font-extrabold">다양한 커리큘럼</span>
          </div>
          {/** 웹 */}
          <div className="desktop:flex desktop:typo-28 desktop:mt-[32px] desktop:gap-4 desktop:mb-20 mt-5 hidden flex-col text-center font-semibold text-[#3F3F3F]">
            <span>
              수강생의 상황과 필요에 맞춰 꼭 필요한 부분만 콕 집어 코칭 받을 수
              있는 합리적인 가격{' '}
            </span>
            <span>
              수강생 84%가 선택한 베스트셀러! 면허 취득부터 운전연수는
              드라이빙존 운전면허
            </span>
          </div>
          {/** 모바일 */}
          <div className="desktop:hidden typo-18 mt-5 mb-10 flex flex-col gap-2 text-center font-semibold text-[#3F3F3F]">
            <span>수강생의 상황과 필요에 맞춰 꼭 필요한 부분만</span>
            <span>콕 집어 코칭 받을 수 있는 합리적인 가격</span>
            <span>수강생 84%가 선택한 베스트셀러! 면허 취득</span>
            <span>부터 운전연수는 드라이빙존 운전면허</span>
          </div>
          <div className="desktop:gap-8 desktop:w-[940px] flex w-full flex-col gap-6">
            <PracticeProgramCard data={week2CardData} />
            <PracticeProgramCard data={monthCardData} />
          </div>
          <span className="typo-14 desktop:typo-18 text-level-6 desktop:w-[940px] desktop:mt-4 desktop:p-0 w-full pt-4 pr-5 pb-[30px] text-right font-semibold">
            ※ 모든 이용료는 부가세 별도입니다.
          </span>
          <Link href="/pricing" className="cursor-pointer">
            {/**웹 */}
            <ImageOptimizer
              src="/images/recommend-class/common/fare_btn.svg"
              alt="더 많은 커리큘럼 보러가기 버튼"
              width={424}
              height={104}
              className="desktop:block mt-[26px] hidden"
            />
            {/** 모바일 */}
            <ImageOptimizer
              src="/images/recommend-class/common/mo_fare_btn.svg"
              alt="더 많은 커리큘럼 보러가기 버튼"
              width={280}
              height={80}
              className="desktop:hidden"
            />
          </Link>
        </div>
      </div>
    </div>
  );
}
